import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PublicReportResponse, ReportApplication, ReportFilters } from '@app/models/reports';
import { environment } from '@env/environment';
import { ProcessorReportFilters } from '@src/app/LAUSD/agency/agency-reports/processor-report/processor-report.component';
import dayjs from 'dayjs';
import { Observable, tap } from 'rxjs';
import { downloadBlob } from 'src/assets/helpers/downloadFile';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  constructor(private http: HttpClient) {}

  runAgencyReport(reportFilters: ReportFilters): Observable<ReportApplication[][]> {
    return this.http.get<ReportApplication[][]>(`${environment.apiUrl}/api/Report/GetReportTable`, {
      params: { ...reportFilters },
    });
  }

  runPublicReport(questionnaireType: number, agencyToken: string): Observable<PublicReportResponse> {
    return this.http.get<PublicReportResponse>(`${environment.apiUrl}/api/Report/GetPublicReportTable`, {
      params: { questionnaireType, agencyToken },
    });
  }

  // Anonymous download for public report links ending in /pdf or /excel.
  downloadPublicReport(questionnaireType: number, agencyToken: string, format: 'pdf' | 'excel') {
    return this.http
      .get(`${environment.apiUrl}/api/Report/DownloadPublicReport`, {
        params: { questionnaireType, agencyToken, format },
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(tap(downloadBlob()));
  }

  /** The user can run the report with filters using `runAgencyReport` or download the current report using this method. */
  downloadCurrentReport(reportFilters: ReportFilters, isPdf = false) {
    return this.http
      .get(`${environment.apiUrl}/api/Report/DownloadFilteredReport`, {
        params: {
          ...reportFilters,
          pdfFormat: isPdf,
        },
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        tap(
          downloadBlob(
            reportFilters.groupBy > 0
              ? this.getGroupedReportFileName(reportFilters.groupBy, !isPdf)
              : this.getReportFileName(reportFilters.questionnaireType, !isPdf, true)
          )
        )
      );
  }

  getReportsLastRefreshed(): Observable<string> {
    return this.http.get<string>(`${environment.apiUrl}/api/Report/GetReportsLastRefreshed`);
  }

  getLausdReportsLastRefreshed(): Observable<string> {
    if (environment.site !== 'lausd') {
      throw new Error('Cannot get LAUSD report refresh dates for non-LAUSD site!');
    }
    return this.http.get<string>(`${environment.apiUrl}/api/Report/GetReportsLastRefreshed`);
  }

  /** Refreshes the report  */
  // forceReportRefresh(): Observable<void> {
  //   return this.http.post<void>(`${environment.apiUrl}/api/Report/ForceRunPublicReports`, {});
  // }

  forceLausdReportRefresh(): Observable<void> {
    if (environment.site !== 'lausd') {
      throw new Error('Cannot force LAUSD report refresh for non-LAUSD site!');
    }
    return this.http.post<void>(`${environment.apiUrl}/api/Report/ForceRunPublicReports`, {});
  }

  downloadNightlyReport(questionnaireTypeId: number, pdfFormat: boolean = true): Observable<any> {
    return this.http.get(`${environment.apiUrl}/api/Report/GetReportDownloadLink`, {
      params: { questionnaireTypeId, pdfFormat },
    });
  }

  getProcessorReport(filters: ProcessorReportFilters): Observable<any> {
    return this.http.get(`${environment.apiUrl}/api/Report/GetProcessorReportTable`, {
      params: { startDate: filters.startDate, endDate: filters.endDate },
    });
  }

  downloadProcessorReport(filters: ProcessorReportFilters, type: number, isPDF: boolean) {
    const name: string = `${type === 1 ? 'Application Volume And Quality' : type === 2 ? 'Processing And Timeliness' : type === 3 ? 'Composite Metrics' : type === 4 ? 'Processor Metrics' : 'Processor'} Report_${dayjs().format('YYYY.MM.DD_HH.mm')}.${isPDF ? 'pdf' : 'xlsx'}`;
    return this.http
      .get(`${environment.apiUrl}/api/Report/DownloadProcessorReport`, {
        params: {
          type: type,
          startDate: filters.startDate,
          endDate: filters.endDate,
          isPDF: isPDF,
        },
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(tap(downloadBlob(name)));
  }

  getReportFileName(
    questionnaireTypeId: number,
    // largeFormat: boolean = false,
    isExcel = true,
    showDate = true
  ): string {
    let fileName = 'QB_';
    if (questionnaireTypeId === 1) {
      fileName += 'Prime_';
    } else if (questionnaireTypeId === 2) {
      fileName += 'Sub_';
    } else if (questionnaireTypeId === 3) {
      fileName += 'Safety_';
    } else {
      fileName += 'All_Contractors_';
    }

    fileName += 'Report_';
    // fileName += largeFormat ? 'Report_Large_' : 'Report_';
    if (showDate) {
      fileName += dayjs().format('YYYY.MM.DD_HH.mm');
    }
    fileName += isExcel ? '.xlsx' : '.pdf';
    return fileName;
  }

  getGroupedReportFileName(groupBy: number, isExcel = true): string {
    let fileName = 'QB_';
    if (groupBy === 1) {
      fileName += 'License_Classification_Report_';
    } else if (groupBy === 2) {
      fileName += 'Business_Certification_Report_';
    }
    fileName += dayjs().format('YYYY.MM.DD_HH.mm');
    fileName += isExcel ? '.xlsx' : '.pdf';
    return fileName;
  }
}
